import {
  ArrowRight,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Phone,
  Shield,
  Sparkles,
  User,
  Users,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/Button/Button';
import FlexibleInput from '../../components/Input/Input';
import { Error, Success } from '../../config/helpers/toast-helper';
import { useAffiliateUserStore } from '../../store/affiliate-user';

const Register = () => {
  const { signup, loading } = useAffiliateUserStore();
  const navigate = useNavigate();

  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Field validation states
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // Validation functions
  const validateName = (value) => {
    if (!value.trim()) return 'Name is required';
    if (value.trim().length < 2) return 'Name must be at least 2 characters';
    if (!/^[A-Za-z\s]+$/.test(value))
      return 'Name should contain only letters and spaces';
    return '';
  };

  const validateEmail = (value) => {
    if (!value.trim()) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
    return '';
  };

  const validatePhoneNumber = (value) => {
    if (!value.trim()) return 'Phone number is required';
    // Remove all non-digit characters except + for validation
    const cleanPhone = value.replace(/[^\d+]/g, '');
    if (cleanPhone.length < 10)
      return 'Phone number must be at least 10 digits';
    if (cleanPhone.length > 15) return 'Phone number cannot exceed 15 digits';
    if (!cleanPhone.startsWith('+'))
      return 'Please include country code (e.g., +1)';
    return '';
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters except +
    let cleaned = value.replace(/[^\d+]/g, '');

    // Ensure it starts with +
    if (!cleaned.startsWith('+') && cleaned.length > 0) {
      cleaned = '+' + cleaned;
    }

    // Return as-is without spaces (backend will handle formatting)
    return cleaned.slice(0, 16); // Limit to reasonable length
  };

  const validatePassword = (value) => {
    if (!value) return 'Password is required';
    if (value.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(value))
      return 'Password must include at least one uppercase letter';
    if (!/[a-z]/.test(value))
      return 'Password must include at least one lowercase letter';
    if (!/[0-9]/.test(value))
      return 'Password must include at least one number';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value))
      return 'Password must include at least one special character';
    return '';
  };

  // Real-time validation
  useEffect(() => {
    const newErrors = {};
    if (touchedFields.name) newErrors.name = validateName(name);
    if (touchedFields.email) newErrors.email = validateEmail(email);
    if (touchedFields.phoneNumber)
      newErrors.phoneNumber = validatePhoneNumber(phoneNumber);
    if (touchedFields.password) newErrors.password = validatePassword(password);
    setErrors(newErrors);
  }, [name, email, phoneNumber, password, touchedFields]);

  const handleBlur = (field) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSignup = async () => {
    // Mark all fields as touched for validation
    const allTouched = {
      name: true,
      email: true,
      phoneNumber: true,
      password: true,
    };
    setTouchedFields(allTouched);

    // Validate all fields
    const nameError = validateName(name);
    const emailError = validateEmail(email);
    const phoneError = validatePhoneNumber(phoneNumber);
    const passwordError = validatePassword(password);

    if (nameError || emailError || phoneError || passwordError) {
      setErrors({
        name: nameError,
        email: emailError,
        phoneNumber: phoneError,
        password: passwordError,
      });
      Error('Please fix the form errors before submitting');
      return;
    }

    try {
      const payload = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phoneNumber: phoneNumber.trim(),
        password,
      };

      const response = await signup(payload);

      if (response?.success !== false) {
        Success(
          'Registration successful! Please check your email for verification.'
        );
        localStorage.setItem('affiliateEmail', email);
        navigate('/auth/verify-account');
      }
    } catch (error) {
      console.error('Registration error:', error);
      Error(
        error?.response?.data?.message ||
          'Registration failed. Please try again.'
      );
    }
  };

  const isFormValid =
    !validateName(name) &&
    !validateEmail(email) &&
    !validatePhoneNumber(phoneNumber) &&
    !validatePassword(password);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-green-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-green-400 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-green-600 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 flex">
        {/* Left Side - Info Panel */}
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-center p-12 text-white">
          <div className="max-w-md">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-green-400 to-green-500 rounded-xl flex items-center justify-center mr-4">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-green-400 to-green-500 bg-clip-text text-transparent">
                Partner Program
              </h1>
            </div>

            <h2 className="text-4xl font-bold mb-6 leading-tight">
              Start Earning with Our
              <span className="block bg-gradient-to-r from-green-400 to-green-500 bg-clip-text text-transparent">
                Affiliate Program
              </span>
            </h2>

            <p className="text-lg text-gray-300 mb-8">
              Join thousands of partners earning substantial commissions by
              referring customers to our platform.
            </p>

            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center mr-3">
                  <Shield className="w-4 h-4 text-green-400" />
                </div>
                <span className="text-gray-300">
                  Secure and reliable platform
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center mr-3">
                  <Users className="w-4 h-4 text-green-400" />
                </div>
                <span className="text-gray-300">
                  Growing community of partners
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center mr-3">
                  <ArrowRight className="w-4 h-4 text-green-400" />
                </div>
                <span className="text-gray-300">High conversion rates</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Registration Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md">
            {/* Mobile Header */}
            <div className="lg:hidden text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-green-400 to-green-500 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Join Our Partner Program
              </h1>
              <p className="text-gray-400">
                Create your account and start earning
              </p>
            </div>

            {/* Form Card */}
            <div className="bg-gray-800/30 backdrop-blur-xl rounded-3xl p-8 border border-gray-700/50 shadow-2xl">
              <div className="hidden lg:block text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">
                  Create Account
                </h2>
                <p className="text-gray-400">
                  Fill in your details to get started
                </p>
              </div>

              <div className="space-y-6">
                <div className="space-y-4">
                  <FlexibleInput
                    placeholder="Full Name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={() => handleBlur('name')}
                    icon={User}
                    iconClass="text-green-400"
                    error={errors.name}
                    touched={touchedFields.name}
                    className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400/20"
                  />

                  <FlexibleInput
                    placeholder="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => handleBlur('email')}
                    icon={Mail}
                    iconClass="text-green-400"
                    error={errors.email}
                    touched={touchedFields.email}
                    className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400/20"
                  />

                  <FlexibleInput
                    placeholder="Phone Number (e.g., +12345678900)"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) =>
                      setPhoneNumber(formatPhoneNumber(e.target.value))
                    }
                    onBlur={() => handleBlur('phoneNumber')}
                    icon={Phone}
                    iconClass="text-green-400"
                    error={errors.phoneNumber}
                    touched={touchedFields.phoneNumber}
                    className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400/20"
                  />

                  <div className="relative">
                    <FlexibleInput
                      placeholder="Password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onBlur={() => handleBlur('password')}
                      icon={Lock}
                      iconClass="text-green-400"
                      error={errors.password}
                      touched={touchedFields.password}
                      className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400/20 pr-12"
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute right-3 top-4 text-gray-400 hover:text-white transition-colors z-10"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  text="Create Account"
                  onClick={handleSignup}
                  loading={loading}
                  disabled={!isFormValid}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-0 shadow-lg shadow-green-500/25 hover:shadow-green-500/40 transition-all duration-300"
                  size="large"
                  icon={ArrowRight}
                  iconPosition="right"
                />

                <div className="text-center">
                  <p className="text-gray-400">
                    Already have an account?{' '}
                    <Link
                      to="/auth/login"
                      className="text-green-400 hover:text-green-300 font-medium transition-colors"
                    >
                      Sign In
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mt-6">
              <p className="text-xs text-gray-500 max-w-sm mx-auto">
                By creating an account, you agree to our{' '}
                <Link
                  to="/terms"
                  className="text-green-400 hover:text-green-300 transition-colors"
                >
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link
                  to="/privacy"
                  className="text-green-400 hover:text-green-300 transition-colors"
                >
                  Privacy Policy
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
